import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  InputAdornment,
  Link,
  Menu,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  Search as SearchIcon,
  ViewList as ListIcon,
  ViewKanban as BoardIcon,
  MoreVert as MoreIcon,
} from '@mui/icons-material';
import { applicationsApi } from '../../services/api';
import {
  APPLICATION_STATUSES,
  ApplicationBoard,
  ApplicationListItem,
  ApplicationStatus,
  statusMeta,
} from '../../types';
import { errorMessage } from '../../utils/errors';
import { formatDate, formatRelative } from '../../utils/format';
import { useToast } from '../../hooks/useToast';

type View = 'list' | 'board';

const EMPTY_BOARD = (): ApplicationBoard =>
  APPLICATION_STATUSES.reduce((acc, s) => {
    acc[s.value] = [];
    return acc;
  }, {} as ApplicationBoard);

const ApplicationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { show, toast } = useToast();
  const [view, setView] = useState<View>('list');
  const [statusFilter, setStatusFilter] = useState<ApplicationStatus | ''>('');
  const [search, setSearch] = useState('');

  const [items, setItems] = useState<ApplicationListItem[]>([]);
  const [board, setBoard] = useState<ApplicationBoard>(EMPTY_BOARD);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (view === 'list') {
        setItems(await applicationsApi.list(statusFilter));
      } else {
        const data = await applicationsApi.board();
        setBoard({ ...EMPTY_BOARD(), ...data });
      }
    } catch (e) {
      setError(errorMessage(e, 'Could not load your applications.'));
    } finally {
      setLoading(false);
    }
  }, [view, statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (a) =>
        a.job.title.toLowerCase().includes(q) || a.job.company.name.toLowerCase().includes(q)
    );
  }, [items, search]);

  // The API can return a column for a status this build does not know about
  // (the board endpoint keys by whatever status a row holds). Render those
  // after the known ones rather than dropping their applications.
  const boardColumns = useMemo(() => {
    const extra = Object.keys(board)
      .filter((key) => !APPLICATION_STATUSES.some((s) => s.value === key))
      .map((key) => statusMeta(key));
    return [...APPLICATION_STATUSES, ...extra];
  }, [board]);

  const changeStatus = async (app: ApplicationListItem, status: ApplicationStatus) => {
    if (status === app.status) return;
    setUpdatingId(app.id);
    try {
      const updated = await applicationsApi.patch(app.id, { status });
      if (view === 'list') {
        setItems((prev) =>
          prev
            .map((a) => (a.id === app.id ? { ...a, ...updated, job: updated.job ?? a.job } : a))
            // Drop it from a filtered list if it no longer matches.
            .filter((a) => !statusFilter || a.status === statusFilter)
        );
      } else {
        setBoard((prev) => {
          const next = { ...prev };
          next[app.status] = prev[app.status].filter((a) => a.id !== app.id);
          next[status] = [{ ...app, ...updated, job: updated.job ?? app.job }, ...prev[status]];
          return next;
        });
      }
      show(`Moved to ${statusMeta(status).label}.`);
    } catch (e) {
      show(errorMessage(e, 'Could not update the status.'), 'error');
    } finally {
      setUpdatingId(null);
    }
  };

  const statusSelect = (app: ApplicationListItem) => (
    <Select
      size="small"
      value={app.status}
      onChange={(e) => changeStatus(app, e.target.value as ApplicationStatus)}
      onClick={(e) => e.stopPropagation()}
      disabled={updatingId === app.id}
      sx={{ minWidth: 170, fontSize: '0.875rem' }}
      renderValue={(v) => {
        const meta = statusMeta(String(v));
        return (
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: meta.color }} />
            <span>{meta.label}</span>
          </Stack>
        );
      }}
    >
      {APPLICATION_STATUSES.map((s) => (
        <MenuItem key={s.value} value={s.value}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: s.color }} />
            <span>{s.label}</span>
          </Stack>
        </MenuItem>
      ))}
    </Select>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ md: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h4" component="h1" fontWeight={700} gutterBottom>
            Applications
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Track every application from saved to offer.
          </Typography>
        </Box>
        <ToggleButtonGroup
          value={view}
          exclusive
          size="small"
          onChange={(_, v: View | null) => v && setView(v)}
        >
          <ToggleButton value="list">
            <ListIcon fontSize="small" sx={{ mr: 0.5 }} /> List
          </ToggleButton>
          <ToggleButton value="board">
            <BoardIcon fontSize="small" sx={{ mr: 0.5 }} /> Board
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={load}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {view === 'list' && (
        <>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ md: 'center' }}
            sx={{ mb: 2 }}
          >
            <TextField
              placeholder="Search by title or company"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              size="small"
              sx={{ minWidth: { md: 320 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
              <Chip
                label="All"
                color={statusFilter === '' ? 'primary' : 'default'}
                variant={statusFilter === '' ? 'filled' : 'outlined'}
                onClick={() => setStatusFilter('')}
              />
              {APPLICATION_STATUSES.map((s) => (
                <Chip
                  key={s.value}
                  label={s.label}
                  variant={statusFilter === s.value ? 'filled' : 'outlined'}
                  onClick={() => setStatusFilter(s.value)}
                  sx={
                    statusFilter === s.value
                      ? { bgcolor: s.color, color: 'white', fontWeight: 600 }
                      : undefined
                  }
                />
              ))}
            </Stack>
          </Stack>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : filtered.length === 0 ? (
            <Card variant="outlined" sx={{ textAlign: 'center', py: 6 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {items.length === 0 ? 'No applications yet' : 'No applications match'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {items.length === 0
                    ? 'Applications you fill with the extension appear here automatically.'
                    : 'Try a different search or status filter.'}
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Company</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Applied</TableCell>
                    <TableCell>ATS</TableCell>
                    <TableCell>Resume</TableCell>
                    <TableCell>Next action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map((app) => (
                    <TableRow
                      key={app.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/applications/${app.id}`)}
                    >
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Avatar src={app.job.company.logo_url || undefined} sx={{ width: 24, height: 24, fontSize: 12 }}>
                            {app.job.company.name?.[0] ?? '?'}
                          </Avatar>
                          <Typography variant="body2" fontWeight={500}>
                            {app.job.company.name}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{app.job.title}</Typography>
                        {app.job.location && (
                          <Typography variant="caption" color="text.secondary">
                            {app.job.location}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>{statusSelect(app)}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{formatDate(app.applied_at) || '-'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{app.ats_platform || app.job.source_platform || '-'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{app.resume?.label || '-'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {app.next_action_at ? formatRelative(app.next_action_at) : '-'}
                        </Typography>
                        {app.interview_stage && (
                          <Typography variant="caption" color="text.secondary">
                            {app.interview_stage}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {view === 'board' &&
        (loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : error ? null : (
          // A failed load shows only the error above, not nine empty columns.
          <Box sx={{ overflowX: 'auto', pb: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, minWidth: 'fit-content' }}>
              {boardColumns.map((column) => {
                const apps = board[column.value] ?? [];
                return (
                  <Paper key={column.value} variant="outlined" sx={{ width: 280, flexShrink: 0, bgcolor: 'grey.50' }}>
                    <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: column.color }} />
                        <Typography variant="subtitle2" fontWeight={600}>
                          {column.label}
                        </Typography>
                        <Chip label={apps.length} size="small" sx={{ ml: 'auto', height: 20 }} />
                      </Stack>
                    </Box>
                    <Box sx={{ p: 1, minHeight: 160 }}>
                      {apps.length === 0 ? (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', py: 3 }}>
                          Nothing here
                        </Typography>
                      ) : (
                        apps.map((app) => (
                          <BoardCard
                            key={app.id}
                            app={app}
                            busy={updatingId === app.id}
                            to={`/applications/${app.id}`}
                            onOpen={() => navigate(`/applications/${app.id}`)}
                            onMove={(status) => changeStatus(app, status)}
                          />
                        ))
                      )}
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          </Box>
        ))}

      {toast}
    </Container>
  );
};

interface BoardCardProps {
  app: ApplicationListItem;
  busy: boolean;
  to: string;
  onOpen: () => void;
  onMove: (status: ApplicationStatus) => void;
}

const BoardCard: React.FC<BoardCardProps> = ({ app, busy, to, onOpen, onMove }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  return (
    <Card
      variant="outlined"
      sx={{ mb: 1, cursor: 'pointer', opacity: busy ? 0.6 : 1, '&:hover': { boxShadow: 2 } }}
      onClick={onOpen}
    >
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="caption" color="text.secondary" noWrap display="block">
              {app.job.company.name}
            </Typography>
            {/* A real link, so the card can be reached by keyboard and opened
                in a new tab. The whole card stays clickable for the mouse. */}
            <Link
              component={RouterLink}
              to={to}
              variant="subtitle2"
              color="inherit"
              underline="hover"
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              sx={{ display: 'block', fontWeight: 600, lineHeight: 1.3, overflowWrap: 'anywhere' }}
            >
              {app.job.title}
            </Link>
          </Box>
          <IconButton
            size="small"
            aria-label="Move to"
            onClick={(e) => {
              e.stopPropagation();
              setAnchorEl(e.currentTarget);
            }}
            disabled={busy}
          >
            <MoreIcon fontSize="small" />
          </IconButton>
        </Stack>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          {app.applied_at ? `Applied ${formatRelative(app.applied_at)}` : `Saved ${formatRelative(app.created_at)}`}
        </Typography>
        {app.notes_preview && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              fontStyle: 'italic',
              mt: 0.5,
            }}
          >
            {app.notes_preview}
          </Typography>
        )}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          onClick={(e) => e.stopPropagation()}
        >
          <MenuItem disabled>
            <Typography variant="caption">Move to...</Typography>
          </MenuItem>
          {APPLICATION_STATUSES.filter((s) => s.value !== app.status).map((s) => (
            <MenuItem
              key={s.value}
              onClick={() => {
                setAnchorEl(null);
                onMove(s.value);
              }}
            >
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: s.color, mr: 1 }} />
              {s.label}
            </MenuItem>
          ))}
        </Menu>
      </CardContent>
    </Card>
  );
};

export default ApplicationsPage;
